import { useEffect, useMemo, useState } from "react";
import {
    Box, Button, Chip, Divider, FormControl, FormControlLabel, Grid, InputAdornment, InputLabel, Link, MenuItem, Select,
    Stack, Switch, Tab, Tabs, TextField, Typography,
} from "@mui/material";
import { AutoStories, Edit, Event, FolderOpen, Lock, Numbers, OpenInFull, Save } from "@mui/icons-material";
import { postData } from "../../services/base.service";
import { useNotification } from "../Shared/NotificationProvider";
import AutocompleteComponent from "../Shared/AutocompleteComponent";
import { ProjectDetail, ProjectOptions, ProjectRecord } from "../../types/projectTypes";
import { conditionColor, numberOrNull, statusColor, toDateInput } from "./projectUtils";
import ProjectTeamTab from "./ProjectTeamTab";
import ProjectPartnersTab from "./ProjectPartnersTab";
import ProjectFinanceTab from "./ProjectFinanceTab";
import ProjectReferenceTab from "./ProjectReferenceTab";
import ProjectRfsTab from "./ProjectRfsTab";
import { useNavigate } from "react-router-dom";

type ProjectFormProps = {
    detail: ProjectDetail;
    options: ProjectOptions;
    setLoading: Function;
    refresh: () => Promise<void>;
    // Lets the drawer widen for wide tabs (Billing).
    onTabChange?: (tab: number) => void;
};

// Editable copy of the project; numbers/dates are kept as strings while editing.
type FormState = Omit<ProjectRecord, 'intee_share' | 'contract_volume' | 'client_ID'> & {
    intee_share_percent: string;
    contract_volume: string;
    client_ID: number | '';
    countryIds: number[];
};

const DATE_FIELDS = [
    'eoi_submitted_date', 'tender_submitted_date', 'date_assignment_commissioned', 'provisional_decision_date',
    'deadline', 'deadline_intern', 'project_start_date', 'project_end_date', 'price_escalation_date',
] as const;

const toFormState = (detail: ProjectDetail): FormState => {
    const p = detail.project;
    const state = {
        ...p,
        intee_share_percent: p.intee_share === null ? '' : String(Math.round(Number(p.intee_share) * 10000) / 100),
        contract_volume: p.contract_volume === null ? '' : String(p.contract_volume),
        client_ID: p.client_ID ?? '',
        countryIds: detail.countries.map((c) => c.id),
    } as FormState;
    DATE_FIELDS.forEach((field) => { (state as any)[field] = toDateInput(p[field]); });
    return state;
};

function ProjectForm({ detail, options, setLoading, refresh, onTabChange }: ProjectFormProps) {
    const notify = useNotification();
    const navigate = useNavigate();
    const [tab, setTab] = useState(0);
    const [isEditMode, setIsEditMode] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState<FormState>(() => toFormState(detail));

    useEffect(() => {
        setForm(toFormState(detail));
    }, [detail]);

    // Switching to another project resets to view mode on the first tab.
    useEffect(() => {
        setIsEditMode(false);
        setTab(0);
        onTabChange?.(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detail.project.ID]);

    // The RfS tab disappears when tracking is switched off.
    useEffect(() => {
        if (!detail.project.rfs_tracking && tab === 6) { setTab(0); onTabChange?.(0); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detail.project.rfs_tracking]);

    const countryOptions = useMemo(() => options.countries, [options.countries]);
    const selectedCountries = countryOptions.filter((c) => form.countryIds.includes(c.id));

    const set = (field: keyof FormState, value: unknown) => setForm((prev) => ({ ...prev, [field]: value }));
    const readOnly = { readOnly: !isEditMode };

    const toggleEditMode = () => {
        if (isEditMode) setForm(toFormState(detail)); // discard unsaved field edits on lock
        setIsEditMode(!isEditMode);
        notify(isEditMode ? 'View mode - unsaved field changes were discarded.' : 'Edit mode - change fields and hit Save. Team, partner and invoice changes are saved immediately.', 'info');
    };

    const handleSave = async () => {
        if (!String(form.project_number || '').trim()) {
            notify('Project number is required.', 'warning');
            return;
        }
        const share = numberOrNull(form.intee_share_percent);
        if (share !== null && (share < 0 || share > 100)) {
            notify('INT share must be between 0 and 100 %.', 'warning');
            return;
        }
        const payload: Record<string, unknown> = {
            project_number: String(form.project_number).trim(),
            project_name: form.project_name || null,
            project_donor_number: form.project_donor_number || null,
            client_contract_number: form.client_contract_number || null,
            billing_type: form.billing_type || null,
            client_ID: form.client_ID === '' ? null : form.client_ID,
            donor: form.donor || null,
            origin_of_tender: form.origin_of_tender || null,
            lead: !!form.lead,
            rfs_tracking: !!form.rfs_tracking,
            intee_share: share === null ? null : share / 100,
            contract_volume: numberOrNull(form.contract_volume),
            currency: form.currency || null,
            status: form.status || null,
            project_condition: form.project_condition || null,
            closure_outcome: form.closure_outcome || null,
            remark: form.remark || null,
            folder: form.folder || null,
            countryIds: form.countryIds,
        };
        DATE_FIELDS.forEach((field) => { payload[field] = form[field] || null; });

        setSaving(true);
        setLoading(true);
        try {
            await postData(`projects/update/${detail.project.ID}`, payload);
            notify('Project saved.', 'success');
            setIsEditMode(false);
            await refresh();
        } catch (error) {
            notify(`Could not save: ${(error as Error).message}`, 'error');
        } finally {
            setSaving(false);
            setLoading(false);
        }
    };

    const dateField = (field: typeof DATE_FIELDS[number], label: string) => (
        <TextField fullWidth type="date" label={label} value={form[field] || ''} onChange={(e) => set(field, e.target.value)}
            InputLabelProps={{ shrink: true }} InputProps={readOnly} />
    );

    return (
        <div>
            <Box sx={{ position: 'sticky', top: 0, background: 'white', zIndex: 1100, borderBottom: 1, borderColor: 'divider', marginX: -2, paddingX: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" paddingY={1} gap={1}>
                    <Stack direction="row" gap={1}>
                        <Button variant="outlined" startIcon={isEditMode ? <Lock /> : <Edit />} onClick={toggleEditMode}>
                            {isEditMode ? 'Lock' : 'Edit'}
                        </Button>
                        {isEditMode && (
                            <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving}>Save</Button>
                        )}
                        {/* <Button variant="outlined" startIcon={<OpenInFull />} onClick={() => navigate(`/projects/${detail.project.ID}/controlling`)}>
                            Billing and margin
                        </Button> */}
                    </Stack>
                    <Box sx={{ textAlign: 'right', minWidth: 0 }}>
                        <Typography variant="h6" color="primary" noWrap>{detail.project.project_number} - {detail.project.project_name}</Typography>
                        <Stack direction="row" gap={0.5} justifyContent="flex-end">
                            <Chip size="small" label={detail.project.status || 'No status'} color={statusColor(detail.project.status)} />
                            {detail.project.project_condition && (
                                <Chip size="small" variant="outlined" label={detail.project.project_condition}
                                    sx={{ borderColor: conditionColor(detail.project.project_condition), color: conditionColor(detail.project.project_condition) }} />
                            )}
                        </Stack>
                    </Box>
                </Stack>
                <Tabs value={tab} onChange={(_, value) => { setTab(value); onTabChange?.(value); }} variant="scrollable" scrollButtons="auto">
                    <Tab label="Overview" />
                    <Tab label={`Team (${detail.staff.length + detail.experts.length})`} />
                    <Tab label={`Partners (${detail.partners.length})`} />
                    <Tab label={`Finance (${detail.invoices.length})`} />
                    <Tab label="Tender" />
                    <Tab label="Reference" />
                    {detail.project.rfs_tracking && <Tab label={`RfS (${detail.rfs.length})`} />}
                </Tabs>
            </Box>

            {tab === 0 && (
                <Grid container spacing={2.5} paddingTop={2}>
                    <Grid item xs={12}>
                        <Stack direction="row" alignItems="center" gap={1}><AutoStories /><Typography variant="h6">General</Typography></Stack>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField fullWidth required label="INT #" value={form.project_number || ''} onChange={(e) => set('project_number', e.target.value)}
                            InputLabelProps={{ shrink: true }} InputProps={readOnly} />
                    </Grid>
                    <Grid item xs={12} md={9}>
                        <TextField fullWidth multiline label="Project name" value={form.project_name || ''} onChange={(e) => set('project_name', e.target.value)}
                            InputLabelProps={{ shrink: true }} InputProps={readOnly} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel shrink>Status</InputLabel>
                            <Select notched label="Status" value={form.status || ''} onChange={(e) => set('status', e.target.value)} readOnly={!isEditMode}>
                                <MenuItem value="">-</MenuItem>
                                {/* keep legacy values selectable so old projects still display */}
                                {Array.from(new Set([...options.statuses, ...(form.status ? [form.status] : [])])).map((s) => (
                                    <MenuItem key={s} value={s}>{s}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel shrink>Condition</InputLabel>
                            <Select notched label="Condition" value={form.project_condition || ''} onChange={(e) => set('project_condition', e.target.value)} readOnly={!isEditMode}>
                                <MenuItem value="">-</MenuItem>
                                {options.conditions.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel shrink>Closure outcome</InputLabel>
                            <Select notched label="Closure outcome" value={form.closure_outcome || ''} onChange={(e) => set('closure_outcome', e.target.value)}
                                readOnly={!isEditMode} disabled={form.status !== 'Closed'}>
                                <MenuItem value="">-</MenuItem>
                                <MenuItem value="Successful">Successful</MenuItem>
                                <MenuItem value="Terminated">Terminated</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel shrink>Client</InputLabel>
                            <Select notched label="Client" value={form.client_ID} onChange={(e) => set('client_ID', e.target.value)} readOnly={!isEditMode}>
                                <MenuItem value="">-</MenuItem>
                                {options.clients.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth label="Client / donor (text)" value={form.donor || ''} onChange={(e) => set('donor', e.target.value)}
                            helperText="Used when no client is selected" InputLabelProps={{ shrink: true }} InputProps={readOnly} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControlLabel sx={{ marginTop: 1 }}
                            control={<Switch checked={!!form.lead} onChange={(e) => isEditMode && set('lead', e.target.checked)} />}
                            label={form.lead ? 'INT leads the consortium' : 'INT is a partner'} />
                    </Grid>
                    <Grid item xs={12}>
                        {isEditMode ? (
                            <AutocompleteComponent isMulti label="Countries" options={countryOptions} value={selectedCountries}
                                getOptionLabel={(o: { name: string }) => o?.name ?? ''}
                                isOptionEqualToValue={(o: { id: number }, v: { id: number }) => o.id === v.id}
                                handleChange={(_: any, value: { id: number }[]) => set('countryIds', value.map((v) => v.id))} />
                        ) : (
                            <Stack direction="row" gap={0.5} flexWrap="wrap" alignItems="center">
                                <Typography variant="body2" color="text.secondary" sx={{ marginRight: 1 }}>Countries:</Typography>
                                {selectedCountries.length ? selectedCountries.map((c) => <Chip key={c.id} size="small" label={c.name} />) : '-'}
                            </Stack>
                        )}
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel shrink>Billing</InputLabel>
                            <Select notched label="Billing" value={form.billing_type || ''} onChange={(e) => set('billing_type', e.target.value)} readOnly={!isEditMode}>
                                <MenuItem value="">-</MenuItem>
                                <MenuItem value="time_based">Time-based (expert-days, GIZ)</MenuItem>
                                <MenuItem value="lump_sum">Lump sum / milestones (KfW ...)</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth label="Client contract number (GIZ VN)" value={form.client_contract_number || ''}
                            onChange={(e) => set('client_contract_number', e.target.value)} inputProps={{ maxLength: 50 }} InputLabelProps={{ shrink: true }} InputProps={readOnly} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth label="Client project number (GIZ PN)" value={form.project_donor_number || ''}
                            onChange={(e) => set('project_donor_number', e.target.value)} inputProps={{ maxLength: 25 }} InputLabelProps={{ shrink: true }} InputProps={readOnly} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControlLabel sx={{ marginTop: 1 }}
                            control={<Switch checked={!!form.rfs_tracking} onChange={(e) => isEditMode && set('rfs_tracking', e.target.checked)} />}
                            label="Track time per RfS (subtasks)" />
                    </Grid>

                    <Grid item xs={12}><Divider /></Grid>
                    <Grid item xs={12}>
                        <Stack direction="row" alignItems="center" gap={1}><Numbers /><Typography variant="h6">Volume and dates</Typography></Stack>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth type="number" label="Contract volume" value={form.contract_volume} onChange={(e) => set('contract_volume', e.target.value)}
                            helperText={form.lead ? 'Full contract volume' : 'INT share only, as INT is a partner'} InputLabelProps={{ shrink: true }} InputProps={readOnly} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel shrink>Currency</InputLabel>
                            <Select notched label="Currency" value={form.currency || ''} onChange={(e) => set('currency', e.target.value)} readOnly={!isEditMode}>
                                <MenuItem value="">-</MenuItem>
                                <MenuItem value="EUR">EUR</MenuItem>
                                <MenuItem value="USD">USD</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth type="number" label="INT share" value={form.intee_share_percent} onChange={(e) => set('intee_share_percent', e.target.value)}
                            InputLabelProps={{ shrink: true }} InputProps={{ ...readOnly, endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
                    </Grid>
                    <Grid item xs={12} md={4}>{dateField('project_start_date', 'Start date')}</Grid>
                    <Grid item xs={12} md={4}>{dateField('project_end_date', 'End date')}</Grid>
                    <Grid item xs={12} md={4}>{dateField('price_escalation_date', 'Price escalation date')}</Grid>

                    <Grid item xs={12}><Divider /></Grid>
                    <Grid item xs={12}>
                        <Stack direction="row" alignItems="center" gap={1}><FolderOpen /><Typography variant="h6">Notes</Typography></Stack>
                    </Grid>
                    <Grid item xs={12}>
                        {isEditMode ? (
                            <TextField fullWidth label="Folder / SharePoint link" value={form.folder || ''} onChange={(e) => set('folder', e.target.value)} InputLabelProps={{ shrink: true }} />
                        ) : form.folder ? (
                            /^https?:\/\//i.test(form.folder)
                                ? <Link href={form.folder} target="_blank" rel="noopener noreferrer">Open project folder</Link>
                                : <Typography variant="body2">Folder: {form.folder}</Typography>
                        ) : null}
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth multiline minRows={3} label="Remark" value={form.remark || ''} onChange={(e) => set('remark', e.target.value)}
                            InputLabelProps={{ shrink: true }} InputProps={readOnly} />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                            Created {toDateInput(detail.project.date_created) || '-'}{detail.project.created_by ? ` by ${detail.project.created_by}` : ''}
                            {' · '}Last update {toDateInput(detail.project.updated_at) || '-'}{detail.project.updated_by ? ` by ${detail.project.updated_by}` : ''}
                        </Typography>
                    </Grid>
                </Grid>
            )}

            {tab === 1 && <ProjectTeamTab detail={detail} options={options} isEditMode={isEditMode} setLoading={setLoading} refresh={refresh} />}
            {tab === 2 && <ProjectPartnersTab detail={detail} isEditMode={isEditMode} setLoading={setLoading} refresh={refresh} />}
            {tab === 3 && <ProjectFinanceTab detail={detail} isEditMode={isEditMode} setLoading={setLoading} refresh={refresh} />}

            {tab === 4 && (
                <Grid container spacing={2.5} paddingTop={2}>
                    <Grid item xs={12}>
                        <Stack direction="row" alignItems="center" gap={1}><Event /><Typography variant="h6">Tender history</Typography></Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Origin of tender" value={form.origin_of_tender || ''} onChange={(e) => set('origin_of_tender', e.target.value)}
                            inputProps={{ maxLength: 14 }} InputLabelProps={{ shrink: true }} InputProps={readOnly} />
                    </Grid>
                    <Grid item xs={12} md={6}>{dateField('eoi_submitted_date', 'EOI submitted')}</Grid>
                    <Grid item xs={12} md={6}>{dateField('tender_submitted_date', 'Tender submitted')}</Grid>
                    <Grid item xs={12} md={6}>{dateField('deadline', 'Deadline')}</Grid>
                    <Grid item xs={12} md={6}>{dateField('deadline_intern', 'Internal deadline')}</Grid>
                    <Grid item xs={12} md={6}>{dateField('provisional_decision_date', 'Provisional decision')}</Grid>
                    <Grid item xs={12} md={6}>{dateField('date_assignment_commissioned', 'Assignment commissioned')}</Grid>
                </Grid>
            )}

            {tab === 5 && <ProjectReferenceTab detail={detail} setLoading={setLoading} refresh={refresh} />}
            {tab === 6 && detail.project.rfs_tracking && <ProjectRfsTab detail={detail} isEditMode={isEditMode} setLoading={setLoading} refresh={refresh} />}
        </div>
    );
}

export default ProjectForm;
